/**
 * @module beebee-authentication/lib/session-support
 * @description Plain helpers of the session service, kept out of the service
 * so it stays under the god-file ratchet: BeebeeAi history cleanup, the
 * protected organization snapshot, and the in-place re-login lock lookup.
 *
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      lib — pure functions used by services/session.js only
 *   key:       sessionCookieName, clearAiHistory, buildOrgSnapshot, resolveSnapshotArray, flattenIncluded,
 *              findLockService, lockOnScreen, RELOCK_RENDER_WAIT_MS
 *   uses:      -
 *   tests:     session-snapshot-test.js, session-relock-test.js (through the service)
 *   gotchas:   buildOrgSnapshot keeps { id } only — the records are resolved
 *              through store.peekRecord at read time, so pushPayload cannot
 *              corrupt the session getters.
 */

/**
 * Name of the session cookie. Cookies are shared by every port of a host, so in development the apps
 * served on localhost:4200 / 4205 / 4207 … all read and delete the same `secure`: a dev-server reload of
 * one app (an addon rebuild reloads them all) that got a 401 against its own tenant ran logout() and
 * signed out every other app. Development scopes the cookie by port; any other environment keeps
 * `secure` (the beebeeboard.com login page writes it).
 *
 * @param {{ isDevelopment: boolean, _location?: Location }} session
 * @returns {string}
 */
function sessionCookieName(session) {
  const port = session?.isDevelopment ? session._location?.port : '';
  return port ? `secure_${port}` : 'secure';
}

/**
 * localStorage prefix of the BeebeeAi chat history (beebee-base-template
 * bb-ai-panel). The history holds previous answers — patient and revenue data —
 * and is sent back to an external host as context, so it must not outlive the
 * session that wrote it. Keep in sync with AI_HISTORY_PREFIX in bb-ai-panel.js.
 */
const AI_HISTORY_PREFIX = 'beebee-ai-';

/**
 * Remove every BeebeeAi chat history from localStorage, including the legacy
 * per-organization keys written before the history was scoped per user.
 */
function clearAiHistory() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(AI_HISTORY_PREFIX)) keys.push(key);
    }
    keys.forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable (private mode / blocked storage): nothing stored.
  }
}

/**
 * Builds the protected snapshot of an organization record.
 *
 * The snapshot is a lookup INDEX: it holds only { id } for every record of the
 * hasManys, so the existence of a state can be checked even when pushPayload
 * corrupts organization.contact_states in the store. It is never handed out:
 * readers resolve the real Ember Data record through store.peekRecord().
 *
 * @param {Object|null} org - organization record (me.organization)
 * @returns {Object|null} snapshot, null without an organization
 */
function buildOrgSnapshot(org) {
  if (!org) {
    return null;
  }
  // Drop records with no id (new, unsaved or being destroyed): a null id would
  // break store.peekRecord with an assertion.
  const snapshotIds = arr => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(item => item?.id != null).map(item => ({
      id: item.id
    }));
  };
  return {
    id: org.id,
    contact_states: snapshotIds(org.contact_states),
    product_states: snapshotIds(org.product_states),
    event_states: snapshotIds(org.event_states),
    project_states: snapshotIds(org.project_states),
    workhour_states: snapshotIds(org.workhour_states),
    payment_methods: snapshotIds(org.payment_methods),
    organization_custom_fields: snapshotIds(org.organization_custom_fields),
    categories: snapshotIds(org.categories),
    product_categories: snapshotIds(org.product_categories),
    roles: snapshotIds(org.roles)
  };
}

/**
 * Resolves one snapshot array into live Ember Data records (never null).
 *
 * @param {Object|null} snap - the snapshot from buildOrgSnapshot
 * @param {string} key - array name, e.g. 'contact_states'
 * @param {Object} store - the Ember Data store
 * @returns {Array<Model>}
 */
function resolveSnapshotArray(snap, key, store) {
  if (!snap?.[key]) {
    return [];
  }
  // Model type from the key: 'contact_states' → 'contact_state'
  const irregularPlurals = {
    categories: 'category',
    product_categories: 'product_category'
  };
  const modelType = irregularPlurals[key] ?? key.replace(/s$/, '');
  return snap[key].filter(({
    id
  }) => id != null).map(({
    id
  }) => store.peekRecord(modelType, id)).filter(Boolean);
}

/**
 * How long a mid-session expiry waits for the lock screen to appear before it
 * falls back to the login page (apps with their own application template do
 * not render the lock: there the old redirect is the only way out).
 */
const RELOCK_RENDER_WAIT_MS = 1500;

/** The lock markup of beebee-base-template (UI nuova and classic screensaver). */
const LOCK_SELECTOR = '.nv-gate--lock, .screensaver';

/** Is the lock markup in the document? */
function lockOnScreen() {
  return Boolean(document.querySelector(LOCK_SELECTOR));
}

/**
 * The idle-screensaver service that shows the lock, or null when this app has
 * none (then an expired session can only go to the login page).
 *
 * @param {Object|null} owner - getOwner(session)
 */
function findLockService(owner) {
  try {
    return owner?.lookup('service:idle-screensaver') ?? null;
  } catch {
    return null;
  }
}

/**
 * Flattens the first `included` entry of the open-organization payload (an
 * object of nested maps) into a flat record array. A payload without
 * `included` must not throw, otherwise loadOpenTask silently aborts and the
 * organization options never load.
 *
 * @param {Array|undefined} included
 * @returns {Array}
 */
function flattenIncluded(included) {
  const first = included?.[0];
  const out = [];
  for (const key in first) {
    if (!Object.prototype.hasOwnProperty.call(first, key)) continue;
    const value = first[key];
    for (const keyV in value) {
      if (Object.prototype.hasOwnProperty.call(value, keyV)) out.push(value[keyV]);
    }
  }
  return out;
}

export { RELOCK_RENDER_WAIT_MS, buildOrgSnapshot, clearAiHistory, findLockService, flattenIncluded, lockOnScreen, resolveSnapshotArray, sessionCookieName };
//# sourceMappingURL=session-support.js.map
